// Pure-data loader for the spaceship's trimesh collider. Lives alongside
// SpaceshipAssets.ts but imports no THREE — the sim layer (both server and
// client) uses this to build a Rapier trimesh without pulling a renderer
// dependency chain.
//
// Source: scripts/bake-ship-collision.mjs writes public/ship-collision.json
// from the GLB. Re-run that script when the GLB changes.
//
// Browser load: fetch('/ship-collision.json').
// Node load:    fs.readFile('public/ship-collision.json') (server startup
//               does this before the first GameRoom ever runs).

export interface ShipCollisionMesh {
  vertices: Float32Array;
  indices: Uint32Array;
}

interface CollisionJson {
  vertices: number[];
  indices: number[];
  meta?: unknown;
}

let _loaded: ShipCollisionMesh | null = null;
let _loadPromise: Promise<ShipCollisionMesh> | null = null;

const JSON_URL = '/ship-collision.json';
const NODE_FS_PATH = 'public/ship-collision.json';

function toTypedArrays(json: CollisionJson): ShipCollisionMesh {
  return {
    vertices: new Float32Array(json.vertices),
    indices: new Uint32Array(json.indices)
  };
}

async function loadBrowser(): Promise<ShipCollisionMesh> {
  const res = await fetch(JSON_URL);
  if (!res.ok) {
    throw new Error(
      `[SpaceshipCollisionData] fetch ${JSON_URL} failed: ${res.status} ${res.statusText}. ` +
      `Did you run \`node scripts/bake-ship-collision.mjs\`?`
    );
  }
  const json = (await res.json()) as CollisionJson;
  return toTypedArrays(json);
}

// Narrow structural types for the Node builtins we need, declared locally so
// this file compiles under a DOM-only tsconfig (the client build on Vercel
// doesn't pull in @types/node). At runtime these imports are only reached
// via loadNode(), which only runs on the server — the browser takes the
// fetch path and never touches them.
interface NodeFsPromises {
  readFile(path: string, encoding: string): Promise<string>;
}
interface NodePath {
  resolve(...parts: string[]): string;
  dirname(p: string): string;
}
interface NodeUrl {
  fileURLToPath(url: string): string;
}
interface NodeProcess {
  versions?: { node?: string };
  cwd(): string;
}

function getNodeProcess(): NodeProcess | undefined {
  return (globalThis as { process?: NodeProcess }).process;
}

async function loadNode(): Promise<ShipCollisionMesh> {
  // Stash specifiers through a function argument so TS treats them as
  // generic `string`, not string literals — otherwise the compiler tries to
  // resolve `node:fs/promises` etc. at type-check time and fails on a
  // browser tsconfig without @types/node (Vercel's client build, notably).
  // At runtime the bundler sees dynamic import() with a non-literal string
  // and leaves it alone; only the server's Node.js process ever reaches
  // this branch (loadBrowser() handles the browser path via fetch).
  const dyn = (spec: string): Promise<unknown> => import(/* @vite-ignore */ spec);
  const fs = (await dyn('node:fs/promises')) as NodeFsPromises;
  const path = (await dyn('node:path')) as NodePath;
  const url = (await dyn('node:url')) as NodeUrl;
  const proc = getNodeProcess();
  const cwd = proc?.cwd() ?? '.';
  // Resolve relative to the process cwd by default; the server boots from
  // the repo root, so public/ is reachable there. Also fall back to a path
  // relative to this module for editor/test runs.
  const cwdCandidate = path.resolve(cwd, NODE_FS_PATH);
  const parentCandidate = path.resolve(cwd, '..', NODE_FS_PATH);
  const moduleCandidate = (() => {
    try {
      const here = path.dirname(url.fileURLToPath(import.meta.url));
      // shipCollision.ts lives at src/maps/bundles/spaceship-in-rings/ — that's
      // four levels below the repo root, where public/ lives.
      return path.resolve(here, '..', '..', '..', '..', NODE_FS_PATH);
    } catch {
      return cwdCandidate;
    }
  })();
  const candidates = [cwdCandidate, parentCandidate, moduleCandidate];
  let lastErr: unknown = null;
  for (const p of candidates) {
    try {
      const buf = await fs.readFile(p, 'utf8');
      const json = JSON.parse(buf) as CollisionJson;
      return toTypedArrays(json);
    } catch (err) {
      lastErr = err;
    }
  }
  throw new Error(
    `[SpaceshipCollisionData] could not read ${NODE_FS_PATH} from any of ` +
    `[${candidates.join(', ')}]. Did you run \`node scripts/bake-ship-collision.mjs\`? ` +
    `Last error: ${String(lastErr)}`
  );
}

/** Kick off the collision-data load. Idempotent — subsequent calls return
 *  the same promise. Call on the server boot path and on the client's
 *  Saturn map preload path. */
export function preloadShipCollision(): Promise<ShipCollisionMesh> {
  if (_loadPromise) return _loadPromise;
  // Node: process.versions.node is truthy; browsers have no `process` or
  // only a shim (Vite) that lacks `versions.node`. Avoid referencing
  // `window` directly so this module type-checks under Node's no-DOM lib.
  const proc = getNodeProcess();
  const isNode = !!(proc && proc.versions && proc.versions.node);
  _loadPromise = (isNode ? loadNode() : loadBrowser()).then((m) => {
    _loaded = m;
    return m;
  });
  return _loadPromise;
}

/** Synchronous accessor. Returns null if preload hasn't finished. */
export function getShipCollision(): ShipCollisionMesh | null {
  return _loaded;
}
